import { useCallback } from 'react';
import EditSupplierSidePanel from 'src/sections/supplier/EditSupplierSidePanel';
import { mapManageSupplierToOnlineUpsertRequest } from 'src/services/manage_suppliers/mapOfflineUpsertRequest';
import useUpsertOfflineSupplierMutation from 'src/services/manage_suppliers/useUpsertOfflineSupplierMutation';
import { ManageSupplier } from 'src/types/manage-suppliers/ManageSupplier';
import { SupplierFormValues } from 'src/types/manage-suppliers/SupplierForm';
import { useContextSelector } from 'use-context-selector';
import { SuppliersContext } from './SuppliersContext';

export interface EditSupplierSidePanelContainerProps {
  supplier?: ManageSupplier | null;
  isOpen?: boolean;
  onClose?: () => void;
  onSaveSuccess?: () => void | Promise<void>;
  onProgramInfoClick?: (programInfoId: string) => void;
  viewUntilEdit?: boolean;
}

const EditSupplierSidePanelContainer = ({
  supplier: supplierProp,
  isOpen: isOpenProp,
  onClose: onCloseProp,
  onSaveSuccess: onSaveSuccessProp,
  onProgramInfoClick: onProgramInfoClickProp,
  viewUntilEdit,
}: EditSupplierSidePanelContainerProps = {}) => {
  const contextSupplier = useContextSelector(
    SuppliersContext,
    (context) => context.editingSupplier
  );
  const contextIsOpen = useContextSelector(
    SuppliersContext,
    (context) => context.isEditSupplierPanelOpen
  );
  const contextCloseEditSupplierPanel = useContextSelector(
    SuppliersContext,
    (context) => context.closeEditSupplierPanel
  );
  const contextOpenProgramInfo = useContextSelector(
    SuppliersContext,
    (context) => context.openProgramInfo
  );
  const { mutateAsync: upsertOfflineSupplier, isPending: isLoadingManageSupplier } =
    useUpsertOfflineSupplierMutation();

  const useExternalSource = isOpenProp !== undefined;

  const supplier = useExternalSource ? supplierProp ?? null : contextSupplier;
  const isOpen = useExternalSource ? Boolean(isOpenProp) : contextIsOpen;
  const closeEditSupplierPanel = onCloseProp ?? contextCloseEditSupplierPanel;
  const openProgramInfo = onProgramInfoClickProp ?? contextOpenProgramInfo;

  const handleSaveSuccess = useCallback(async () => {
    if (onSaveSuccessProp) {
      await onSaveSuccessProp();
      return;
    }

    if (viewUntilEdit) {
      return;
    }

    closeEditSupplierPanel();
  }, [closeEditSupplierPanel, onSaveSuccessProp, viewUntilEdit]);

  const saveOnlineOverride = useCallback(
    async (values: SupplierFormValues, supplierClientId: number) => {
      if (!supplier) {
        return;
      }

      await upsertOfflineSupplier(
        mapManageSupplierToOnlineUpsertRequest(supplier, values, supplierClientId)
      );
    },
    [supplier, upsertOfflineSupplier]
  );

  return (
    <EditSupplierSidePanel
      isOpen={isOpen}
      supplier={supplier}
      onClose={closeEditSupplierPanel}
      onSaveSuccess={handleSaveSuccess}
      saveOnlineOverride={saveOnlineOverride}
      viewUntilEdit={viewUntilEdit}
      isLoading={isLoadingManageSupplier}
      showProgramInfo
      hasProgramInfo={!!supplier?.programInfo}
      onProgramInfoClick={() => {
        if (supplier?.programInfo) {
          openProgramInfo(supplier.programInfo);
        }
      }}
    />
  );
};

export default EditSupplierSidePanelContainer;
